import React from "react";
import {
  FaReact,
  FaJs,
  FaHtml5,
  FaCss3Alt,
  FaBootstrap,
  FaJava,
} from "react-icons/fa";
import { SiReact, SiTailwindcss, SiCplusplus, SiKotlin } from "react-icons/si";
import { motion } from "framer-motion";

function EducationPart({ setIsOpen }) {
  const CloseModal = () => setIsOpen(true);

  const educationItems = [
    {
      title: "HS of Electrical Engineering, Sarajevo",
      year: "2013 - 2017",
    },
    { title: "Paragon Full Stack Program", year: "2022 - 2023" },
    { title: "International Burch University", year: "2024 - Present" },
  ];

  const skillCategories = [
    {
      category: "Frontend",
      skills: [
        { name: "React.js", icon: <FaReact />, level: 90 },
        { name: "JavaScript", icon: <FaJs />, level: 85 },
        { name: "HTML5", icon: <FaHtml5 />, level: 90 },
        { name: "CSS3", icon: <FaCss3Alt />, level: 85 },
      ],
    },
    {
      category: "Mobile",
      skills: [
        { name: "React Native", icon: <SiReact />, level: 80 },
        { name: "Kotlin", icon: <SiKotlin />, level: 60 },
      ],
    },
    {
      category: "Styling",
      skills: [
        { name: "Tailwind", icon: <SiTailwindcss />, level: 85 },
        { name: "Bootstrap", icon: <FaBootstrap />, level: 80 },
      ],
    },
    {
      category: "Platform",
      skills: [
        { name: "C++", icon: <SiCplusplus />, level: 65 },
        { name: "Java", icon: <FaJava />, level: 60 },
      ],
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemAnim = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  };

  return (
    <div
      className="bg-dark-bg text-slate-200 px-6 md:px-8 py-24 pb-32"
      onClick={CloseModal}
    >
      {/* JOURNEY - vertical timeline */}
      <div className="max-w-4xl mx-auto mb-28">
        <motion.h4
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl font-black text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text mb-16 text-center tracking-tighter font-display"
        >
          Journey
        </motion.h4>

        <div className="relative pl-14 sm:pl-20">
          {/* Vertical line */}
          <div className="absolute left-5 sm:left-8 top-2 bottom-2 w-px bg-slate-800"></div>

          <div className="space-y-10">
            {educationItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                {/* Numbered node */}
                <div className="absolute -left-14 sm:-left-20 top-0 w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-dark-surface border border-slate-700 flex items-center justify-center text-primary-400 font-black font-display text-xs sm:text-lg shadow-lg">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="bg-dark-surface border border-slate-800 rounded-2xl shadow-lg p-6 hover:border-primary-500/50 hover:shadow-primary-500/10 transition-all duration-300">
                  <h5 className="text-xl font-bold text-slate-100 mb-1 font-display">
                    {item.title}
                  </h5>
                  <h6 className="text-primary-400 text-sm font-medium tracking-wide">
                    {item.year}
                  </h6>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* SKILLS - categorized with proficiency bars */}
      <div className="max-w-6xl mx-auto">
        <motion.h4
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl font-black text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text mb-12 text-center tracking-tighter font-display"
        >
          Skills
        </motion.h4>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6"
        >
          {skillCategories.map((group) => (
            <motion.div
              variants={itemAnim}
              key={group.category}
              className="bg-dark-surface/50 border border-slate-800 rounded-3xl p-8 shadow-xl"
            >
              <h5 className="text-primary-400 text-xs font-bold uppercase tracking-widest mb-6">
                {group.category}
              </h5>
              <div className="space-y-5">
                {group.skills.map((skill) => (
                  <div key={skill.name} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-slate-200 font-semibold text-sm">
                        <span className="text-lg text-slate-500 group-hover:text-primary-400 transition-colors">
                          {skill.icon}
                        </span>
                        {skill.name}
                      </div>
                      <span className="text-slate-500 text-xs font-semibold">
                        {skill.level}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-primary-500 to-sky-400 rounded-full"
                      ></motion.div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default EducationPart;
